// =========================================================================
// Resolving the demo's live figures.
//
// `seed/modules.ts` and `seed/areas.ts` carry every interpolated number the
// demo printed as an **inert string** — the original JavaScript expression,
// preserved verbatim so nothing was silently baked in at port time:
//
//     figure: [fx('FLEET.length'), ft(' machines · '), fx('MINE_ORDER.length'), …]
//
// Nothing in the seed layer evaluates those. This module is where they are
// bound, and it binds them by **lookup, not by evaluation**: a table keyed on
// the expression source, each entry a hand-written function over a typed
// context. There is no `eval`, no `new Function`, and no parser.
//
// Why a lookup table rather than an interpreter
// ---------------------------------------------
// An interpreter would have to be trusted with the whole expression language
// and would fail at runtime, inside a render, on anything it did not cover.
// A table fails at *port* time, visibly: an expression with no entry renders
// nothing at all and is listed by {@link unresolvedFigureExpressions}, so the
// gap is a fact somebody can look up rather than a wrong number on screen.
//
// The one deliberate gap
// ----------------------
// `notifications().length` (the customer-notification log) has no engine
// equivalent in this port — the derivation belongs to the customers surface,
// which is another lane's. Its two occurrences resolve to nothing and the
// surrounding literal text is dropped with them, which reads as a shorter
// sentence rather than as a blank.
//
// Transformation layer — pure functions, no React, no side effects.
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { CLASSES, FLEET, HEALTH, MINES, MINE_ORDER } from '@/versions/v1/data/core';
import {
  EXCHANGE,
  FLEET_RULES,
  fmt$,
  fmtk,
  healthBand,
  statusOf,
} from '@/versions/v1/shared/oem-engine';
import { ALERTS } from '@/versions/v1/shared/seed/alerts';
import { CONNECTORS } from '@/versions/v1/shared/seed/connectors';
import { INCIDENTS } from '@/versions/v1/shared/seed/incidents';
import type { AreaStatPart } from '@/versions/v1/shared/seed/areas';
import type { LiveFigure } from '@/versions/v1/shared/seed/modules';
import type { WorkflowStepContext } from '@/versions/v1/shared/seed/workflow-steps';

/** The demo's `ATTN` — the bands that count as needing attention. */
const ATTENTION = new Set(['major', 'vpoor', 'poor']);

/** Locale-pinned grouping, matching the engine's `groupNum`. */
const group = (n: number): string => n.toLocaleString('en-AU');

/**
 * Everything the figure expressions read.
 *
 * `step` carries the twenty-two pack tables, the eight derivations and the ten
 * helpers that {@link useWorkflowStepContext} already assembles. The rest are
 * the handful of tables and derivations the *module* pages reference that no
 * workflow step does, so they are not on the step context.
 */
export interface FigureContext {
  /** The assembled workflow-step context — the bulk of what the table reads. */
  readonly step: WorkflowStepContext;
  /** `B.rms_status` — machines reporting position on the RMS feed. */
  readonly rmsStatus: readonly unknown[];
  /** `B.rms_health` — keyed by machine id. */
  readonly rmsHealth: Readonly<Record<string, unknown>>;
  /** `B.kb` — knowledge-base articles. */
  readonly kb: readonly unknown[];
  /** `B.maximo_assets` — the customer's own EAM register. */
  readonly maximoAssets: readonly unknown[];
  /** `B.tyres.rows` — columnar; column 10 is the repeat-damage flag. */
  readonly tyreRows: readonly (readonly unknown[])[];
  /** `dispatchProposals()`. */
  readonly dispatchProposals: readonly unknown[];
  /** `promises()`, each carrying whether the fix lands inside the window. */
  readonly promises: readonly { readonly inside: boolean }[];
  /** `pmDue(14)`. */
  readonly pmDue14: readonly unknown[];
  /** `B.oil` — oil samples, each with a verdict. */
  readonly oil: readonly unknown[];
}

/** One resolvable expression. */
type Resolver = (c: FigureContext) => string;

/**
 * Expression source → what it computes.
 *
 * Keys are **verbatim** from the seed, including spacing, so a key can be
 * grepped in `modules.ts` or `areas.ts` and matched by eye. Grouped the way
 * the demo's own globals were.
 */
const RESOLVERS: Readonly<Record<string, Resolver>> = {
  // ---- eager core -----------------------------------------------------
  'FLEET.length': () => String(FLEET.length),
  'MINE_ORDER.length': () => String(MINE_ORDER.length),
  'HEALTH.length': () => String(HEALTH.length),
  'Object.keys(CLASSES_).length': () => String(Object.keys(CLASSES).length),
  'new Set(FLEET.map(f=>f.operated_by)).size': () =>
    String(new Set(FLEET.map((f) => f.operated_by)).size),
  'HEALTH.filter(h=>ATTN.includes(h.band)).length': () =>
    String(HEALTH.filter((h) => ATTENTION.has(healthBand(h))).length),
  'HEALTH.filter(h=>h.health_score<=40).length': () =>
    String(HEALTH.filter((h) => h.health_score <= 40).length),

  // ---- narrative seeds ------------------------------------------------
  'ALERTS.length': () => String(ALERTS.length),
  'ALERTS.reduce((a,x)=>a+x.chain.length,0)': () =>
    String(ALERTS.reduce((a, x) => a + x.chain.length, 0)),
  'ALERTS.filter(a=>a.chain.some(c=>/reserved|Reserved/.test(c[1]))).length': () =>
    String(
      // The demo indexed the chain tuple: `c[1]` is the step's output.
      ALERTS.filter((a) => a.chain.some((c) => /reserved|Reserved/.test(c.output))).length,
    ),
  'INCIDENTS.length': () => String(INCIDENTS.length),
  'CONN.length': () => String(CONNECTORS.length),
  'FLEET_RULES.length': () => String(FLEET_RULES.length),
  'EXCHANGE.length': () => String(EXCHANGE.length),

  // ---- work orders ----------------------------------------------------
  'WOS.length.toLocaleString()': (c) => group(c.step.workOrders.length),
  'WOS.filter(w=>/^Closed/.test(w.status)).length.toLocaleString()': (c) =>
    group(c.step.workOrders.filter((w) => /^Closed/.test(w.status)).length),
  'WOS.filter(w=>w.parts_used).length.toLocaleString()': (c) =>
    group(c.step.workOrders.filter((w) => hasText(w, 'parts_used')).length),
  'WOS.filter(w=>w.root_cause).length': (c) =>
    String(c.step.workOrders.filter((w) => hasText(w, 'root_cause')).length),

  // ---- shift record ---------------------------------------------------
  'SD.length.toLocaleString()': (c) => group(c.step.shiftDays.length),

  // ---- the data pack --------------------------------------------------
  'Object.keys(B.mx).length': (c) => String(Object.keys(c.step.pack.mx).length),
  'Object.values(B.mx).reduce((a,m)=>a+(m.children||[]).length,0)': (c) =>
    String(sumNested(c.step.pack.mx, 'children')),
  'Object.values(B.mx).reduce((a,m)=>a+(m.failurereports||[]).length,0)': (c) =>
    String(sumNested(c.step.pack.mx, 'failurereports')),
  'B.mx_contracts.length': (c) => String(c.step.pack.mx_contracts.length),
  'B.mx_pm.length': (c) => String(c.step.pack.mx_pm.length),
  'B.inventory.length': (c) => String(c.step.pack.inventory.length),
  'B.parts.length': (c) => String(c.step.pack.parts.length),
  'B.bulletins.length': (c) => String(c.step.pack.bulletins.length),
  'B.cases.length': (c) => String(c.step.pack.cases.length),
  'B.catbase_bom.length': (c) => String(c.step.pack.catbase_bom.length),
  'B.tapp_jobs.length': (c) => String(c.step.pack.tapp_jobs.length),
  'B.tapp_techs.length': (c) => String(c.step.pack.tapp_techs.length),
  'B.xreach_sessions.length': (c) => String(c.step.pack.xreach_sessions.length),
  'B.css.rows.length': (c) => String(c.step.pack.css.rows.length),
  'B.lms.length': (c) => String(c.step.pack.lms.length),
  'B.kb.length': (c) => String(c.kb.length),
  'B.maximo_assets.length': (c) => String(c.maximoAssets.length),
  'B.rms_status.length': (c) => String(c.rmsStatus.length),
  'Object.keys(B.rms_health).length': (c) => String(Object.keys(c.rmsHealth).length),

  'B.cases.filter(c=>/Open/.test(c.status)).length': (c) =>
    String(c.step.pack.cases.filter((x) => /Open/.test(x.status)).length),
  'B.cases.filter(c=>/Warranty/.test(c.type)).length': (c) =>
    String(c.step.pack.cases.filter((x) => /Warranty/.test(x.type)).length),
  'B.orders.filter(o=>/agent/i.test(o.status)).length': (c) =>
    String(c.step.pack.orders.filter((o) => /agent/i.test(o.status)).length),
  'B.orders.filter(o=>/air/i.test(o.status)).length': (c) =>
    String(c.step.pack.orders.filter((o) => /air/i.test(o.status)).length),
  'B.tapp_jobs.filter(j=>j.customer_signoff).length': (c) =>
    String(c.step.pack.tapp_jobs.filter((j) => truthy(j, 'customer_signoff')).length),
  'B.tapp_jobs.reduce((a,j)=>a+(j.labour_lines||[]).length,0)': (c) =>
    String(c.step.pack.tapp_jobs.reduce((a, j) => a + countNested(j, 'labour_lines'), 0)),
  'B.tapp_techs.filter(t=>/off roster/.test(t.status)).length': (c) =>
    String(c.step.pack.tapp_techs.filter((t) => /off roster/.test(t.status)).length),
  'B.timesheets.reduce((a,t)=>a+t.job_hours,0).toFixed(0)': (c) =>
    c.step.pack.timesheets.reduce((a, t) => a + t.job_hours, 0).toFixed(0),
  'B.timesheets.reduce((a,t)=>a+t.travel_hours,0).toFixed(0)': (c) =>
    c.step.pack.timesheets.reduce((a, t) => a + t.travel_hours, 0).toFixed(0),
  'B.events_daily.rows.reduce((a,r)=>a+r[4],0).toLocaleString()': (c) =>
    group(c.step.pack.events_daily.rows.reduce((a, r) => a + r[4], 0)),
  'B.tyres.rows.filter(r=>r[10]).length': (c) =>
    String(c.tyreRows.filter((r) => Boolean(r[10])).length),
  '[...new Set(B.inventory.map(i=>i.location))].length': (c) =>
    String(new Set(c.step.pack.inventory.map((i) => i.location)).size),

  // ---- remote support -------------------------------------------------
  '[...new Set(B.xreach_sessions.map(s=>s.expert))].length': (c) =>
    String(new Set(c.step.pack.xreach_sessions.map((s) => s.expert)).size),
  'B.xreach_sessions.filter(s=>/Escalated/.test(s.outcome)).length': (c) =>
    String(c.step.pack.xreach_sessions.filter((s) => /Escalated/.test(text(s, 'outcome'))).length),
  'B.xreach_sessions.reduce((a,s)=>a+s.travel_avoided_km,0).toLocaleString()': (c) =>
    group(c.step.pack.xreach_sessions.reduce((a, s) => a + num(s, 'travel_avoided_km'), 0)),
  'Math.round(B.xreach_sessions.filter(x=>x.first_time_fix).length/B.xreach_sessions.length*100)':
    (c) => {
      const sessions = c.step.pack.xreach_sessions;
      if (sessions.length === 0) return '0';
      return String(
        Math.round((sessions.filter((s) => truthy(s, 'first_time_fix')).length / sessions.length) * 100),
      );
    },

  // ---- customer satisfaction (columnar; column 10 is `Overall`) --------
  '(B.css.rows.reduce((a,r)=>a+r[10],0)/B.css.rows.length).toFixed(1)': (c) =>
    meanOverall(c).toFixed(1),
  '(B.css.rows.map(r=>r[10]).reduce((a,b)=>a+b,0)/B.css.rows.length).toFixed(1)': (c) =>
    meanOverall(c).toFixed(1),
  'B.css.rows.filter(r=>r[10]<3.5).length': (c) =>
    String(c.step.pack.css.rows.filter((r) => r[10] < 3.5).length),

  // ---- engine derivations ---------------------------------------------
  'jobs().length': (c) => String(c.step.derived.jobs.length),
  'jobs().filter(j=>j.machine).length': (c) =>
    String(c.step.derived.jobs.filter((j) => Boolean(j.machine)).length),
  'jobs().filter(j=>/Approve|approval|Planner/i.test(j.next)).length': (c) =>
    String(c.step.derived.jobs.filter((j) => /Approve|approval|Planner/i.test(j.next ?? '')).length),
  'predictedUsage().length': (c) => String(c.step.derived.predictedUsage.length),
  'predictedUsage().filter(r=>r.gap>0).length': (c) =>
    String(c.step.derived.predictedUsage.filter((r) => r.gap > 0).length),
  'predictedUsage().filter(r=>/Transfer/.test(r.prop)).length': (c) =>
    String(c.step.derived.predictedUsage.filter((r) => /Transfer/.test(r.prop ?? '')).length),
  'pastInterval().length': (c) => String(c.step.derived.pastInterval.length),
  'warrantyActive().length': (c) => String(c.step.derived.warrantyActive.length),
  'FLEET.length-warrantyActive().length': (c) =>
    String(FLEET.length - c.step.derived.warrantyActive.length),
  'upgradeCandidates().length': (c) => String(c.step.derived.upgradeCandidates.length),
  'renewals().filter(r=>r.days<=365).length': (c) =>
    String(c.step.derived.renewals.filter((r) => r.days <= 365).length),
  'meanUtil()': (c) => String(c.step.derived.meanUtil),
  'fmtk(revenue().parts18)': (c) => fmtk(c.step.derived.revenue.parts18),
  'fmtk(revenue().freightBd)': (c) => fmtk(c.step.derived.revenue.freightBd),
  'dispatchProposals().length': (c) => String(c.dispatchProposals.length),
  'promises().length': (c) => String(c.promises.length),
  'promises().filter(p=>p.inside).length': (c) =>
    String(c.promises.filter((p) => p.inside).length),
  'pmDue(14).length': (c) => String(c.pmDue14.length),

  // ---- the fleet, sliced by site and condition -------------------------
  "FLEET.filter(f=>MINES[f.mine].data_mode==='Live').length": () =>
    String(FLEET.filter((f) => MINES[f.mine].data_mode === 'Live').length),
  "FLEET.filter(f=>MINES[f.mine].data_mode!=='Live').length": () =>
    String(FLEET.filter((f) => MINES[f.mine].data_mode !== 'Live').length),
  "FLEET.filter(f=>['major','vpoor'].includes(STATUS(f.machine_id))).length": () =>
    String(FLEET.filter((f) => SEVERE.has(statusOf(f.machine_id))).length),

  // ---- work orders, sliced ---------------------------------------------
  "WOS.filter(w=>w.status.startsWith('Draft')).length": (c) =>
    String(c.step.workOrders.filter((w) => w.status.startsWith('Draft')).length),
  "WOS.filter(w=>w.mine==='GD').length.toLocaleString()": (c) =>
    group(c.step.workOrders.filter((w) => w.mine === 'GD').length),
  "WOS.filter(w=>w.mine!=='GD').length.toLocaleString()": (c) =>
    group(c.step.workOrders.filter((w) => w.mine !== 'GD').length),
  "WOS.filter(w=>/^Closed/.test(w.status)&&w.created>='2026-09-01').length": (c) =>
    String(
      c.step.workOrders.filter((w) => /^Closed/.test(w.status) && w.created >= '2026-09-01')
        .length,
    ),
  "WOS.filter(w=>w.wo_type==='Breakdown'&&w.created>='2026-06-01').length": (c) =>
    String(
      c.step.workOrders.filter((w) => w.wo_type === 'Breakdown' && w.created >= '2026-06-01')
        .length,
    ),
  "WOS.filter(w=>w.priority.startsWith('1')&&/Draft|Released|Scheduled/.test(w.status)).length":
    (c) =>
      String(
        c.step.workOrders.filter(
          (w) => w.priority.startsWith('1') && /Draft|Released|Scheduled/.test(w.status),
        ).length,
      ),
  'fmt$(WOS.filter(w=>w.status.startsWith(\'Draft\')).reduce((a,w)=>a+w.total_direct_cost_aud,0))':
    (c) =>
      fmt$(
        c.step.workOrders
          .filter((w) => w.status.startsWith('Draft'))
          .reduce((a, w) => a + w.total_direct_cost_aud, 0),
      ),

  // ---- alerts and jobs, sliced -----------------------------------------
  "ALERTS.filter(a=>a.sev==='crit').length": () =>
    String(ALERTS.filter((a) => a.sev === 'crit').length),
  // The demo indexed the chain tuple: `c[0]` is the agent, `c[2]` the status.
  "ALERTS.filter(a=>a.chain.some(c=>/Parts/.test(c[0])&&c[2]==='done')).length": () =>
    String(
      ALERTS.filter((a) => a.chain.some((c) => /Parts/.test(c.agent) && c.status === 'done'))
        .length,
    ),
  "jobs().filter(j=>j.sev==='crit').length": (c) =>
    String((c.step.derived.jobs as readonly { sev?: string }[]).filter((j) => j.sev === 'crit').length),

  // ---- the technician app ----------------------------------------------
  "B.tapp_jobs.filter(j=>j.status==='Completed').length": (c) =>
    String(c.step.pack.tapp_jobs.filter((j) => j.status === 'Completed').length),
  "B.tapp_jobs.filter(j=>j.status!=='Completed').length": (c) =>
    String(c.step.pack.tapp_jobs.filter((j) => j.status !== 'Completed').length),
  "B.tapp_jobs.filter(j=>j.status==='In progress').length": (c) =>
    String(c.step.pack.tapp_jobs.filter((j) => j.status === 'In progress').length),
  "B.tapp_techs.filter(t=>t.status==='On site').length": (c) =>
    String(c.step.pack.tapp_techs.filter((t) => t.status === 'On site').length),

  // ---- stores, training, oil, documents, remote ------------------------
  "B.inventory.filter(i=>i.qty_on_hand<i.min_level&&i.location.includes('site')).length": (c) =>
    String(
      c.step.pack.inventory.filter(
        (i) => i.qty_on_hand < i.min_level && i.location.includes('site'),
      ).length,
    ),
  "B.lms.filter(r=>r.status!=='Current').length": (c) =>
    String(c.step.pack.lms.filter((r) => r.status !== 'Current').length),
  "B.lms.filter(r=>r.status!=='Current'&&r.org==='Meridian Equipment').length": (c) =>
    String(
      c.step.pack.lms.filter((r) => r.status !== 'Current' && r.org === 'Meridian Equipment')
        .length,
    ),
  "B.oil.filter(o=>o.verdict!=='Normal').length": (c) =>
    String(c.oil.filter((o) => text(o, 'verdict') !== 'Normal').length),
  "B.docs.filter(d=>d.file.startsWith('SP-')).length": (c) =>
    String(c.step.pack.docs.filter((d) => text(d, 'file').startsWith('SP-')).length),
  "B.xreach_sessions.filter(s=>s.started>='2026-09-01').length": (c) =>
    String(c.step.pack.xreach_sessions.filter((s) => s.started >= '2026-09-01').length),

  // ---- the contract vocabulary, joined ---------------------------------
  "[...new Set(B.mx_contracts.map(c=>c.type.split(' — ')[0].split(',')[0]))].join(', ')": (c) =>
    [
      ...new Set(
        c.step.pack.mx_contracts.map((x) => x.type.split(' — ')[0].split(',')[0]),
      ),
    ].join(', '),
};

/** The two bands the demo called "very poor or worse". */
const SEVERE = new Set(['major', 'vpoor']);

/**
 * Expressions this port does not compute.
 *
 * One entry, and it is a lane boundary rather than an oversight: the
 * customer-notification log is derived on the customers surface. Exported so
 * the omission is discoverable from code rather than only from a comment.
 */
export const unresolvedFigureExpressions: readonly string[] = ['notifications().length'];

/* ---- small readers for the loosely-typed pack rows ---- */

const field = (row: unknown, key: string): unknown =>
  typeof row === 'object' && row !== null ? (row as Record<string, unknown>)[key] : undefined;

const truthy = (row: unknown, key: string): boolean => Boolean(field(row, key));

const text = (row: unknown, key: string): string => String(field(row, key) ?? '');

const num = (row: unknown, key: string): number => Number(field(row, key) ?? 0);

/** The demo's `w.parts_used` / `w.root_cause` truthiness: set and non-empty. */
const hasText = (row: unknown, key: string): boolean => {
  const v = field(row, key);
  return v !== undefined && v !== null && v !== '';
};

const countNested = (row: unknown, key: string): number => {
  const v = field(row, key);
  return Array.isArray(v) ? v.length : 0;
};

const sumNested = (map: Readonly<Record<string, unknown>>, key: string): number =>
  Object.values(map).reduce((a: number, m) => a + countNested(m, key), 0);

const meanOverall = (c: FigureContext): number => {
  const rows = c.step.pack.css.rows;
  if (rows.length === 0) return 0;
  return rows.reduce((a, r) => a + r[10], 0) / rows.length;
};

/**
 * Resolve one expression, or `null` when the table has no entry.
 *
 * A resolver that throws on unexpected data returns `null` too: a figure is
 * never worth taking a page down for.
 */
export function resolveExpression(expr: string, ctx: FigureContext): string | null {
  const resolver = RESOLVERS[expr];
  if (!resolver) return null;
  try {
    return resolver(ctx);
  } catch {
    return null;
  }
}

/**
 * Render a {@link LiveFigure} to a string.
 *
 * Literal segments are copied character for character. An expression segment
 * with no resolver **and the literal text that follows it** are dropped
 * together, so `"3 sites · {unresolved} machines"` reads as `"3 sites"` rather
 * than as `"3 sites ·  machines"`. Trailing separators are trimmed.
 */
export function renderFigure(figure: LiveFigure, ctx: FigureContext): string {
  const out: string[] = [];
  let dropNextText = false;

  for (const segment of figure) {
    if ('text' in segment) {
      if (dropNextText) {
        dropNextText = false;
        continue;
      }
      out.push(segment.text);
      continue;
    }
    const value = resolveExpression(segment.expr, ctx);
    if (value === null) {
      // Drop the number and the phrase it belonged to.
      while (out.length > 0 && /[·—,]\s*$/.test(out[out.length - 1])) out.pop();
      dropNextText = true;
      continue;
    }
    out.push(value);
  }

  return out.join('').replace(/\s*[·—,]\s*$/, '').trim();
}

/** The same, for `areas.ts`'s `stat` parts, which use a different segment shape. */
export function renderStat(stat: readonly AreaStatPart[], ctx: FigureContext): string {
  return renderFigure(
    stat.map((part) => (part.kind === 'text' ? { text: part.text } : { expr: part.expr })),
    ctx,
  );
}
